// src/components/Gallery.jsx
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import API_BASE_URL from '../config/api';

const Gallery = () => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch events with images
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      console.log('🔄 Gallery: Fetching events...');
      const res = await fetch(`${API_BASE_URL}/api/events`);
      const data = await res.json();
      console.log('📥 Gallery: Received events:', data);
      console.log('📸 Gallery: Events with images:', data.filter(e => e.images && e.images.length > 0));
      setEvents(data);
    } catch (err) {
      console.error('❌ Gallery: Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  // Static gallery items (your original videos and images)
  const staticGalleryItems = [
    // Featured Video (First item)
    {
      id: 'static-0',
      type: 'video',
      src: '/gallery/featured-video.mp4',
      thumbnail: '/gallery/video-thumb.jpg',
      isFeatured: true,
      title: 'Featured Event'
    },
    // Static Gallery Images/Videos
    {
      id: 'static-1',
      type: 'image',
      src: '/gallery/event1.jpg',
      title: 'Event Gallery'
    },
    {
      id: 'static-2',
      type: 'image',
      src: '/gallery/event2.jpg',
      title: 'Event Gallery'
    },
    {
      id: 'static-3',
      type: 'video',
      src: '/gallery/event3.mp4',
      thumbnail: '/gallery/event3-thumb.jpg',
      title: 'Event Video'
    },
    {
      id: 'static-4',
      type: 'image',
      src: '/gallery/event4.jpg',
      title: 'Event Gallery'
    },
    {
      id: 'static-5',
      type: 'image',
      src: '/gallery/event5.jpg',
      title: 'Event Gallery'
    },
    {
      id: 'static-6',
      type: 'video',
      src: '/gallery/event6.mp4',
      thumbnail: '/gallery/event6-thumb.jpg',
      title: 'Event Video'
    },
    {
      id: 'static-7',
      type: 'image',
      src: '/gallery/event7.jpg',
      title: 'Event Gallery'
    },
    {
      id: 'static-8',
      type: 'image',
      src: '/gallery/event8.jpg',
      title: 'Event Gallery'
    }
  ];

  // Convert event images to gallery items
  const dynamicGalleryItems = events.flatMap(event => {
    console.log('🔍 Processing event:', event.name, 'Images:', event.images);
    
    if (!event.images || event.images.length === 0) {
      console.log('⚠️ Event has no images:', event.name);
      return [];
    }
    
    return event.images.map((image, idx) => {
      console.log('✅ Adding image to gallery:', image.url);
      return {
        id: `${event._id}-${idx}`,
        type: 'image',
        src: image.url,
        title: event.name,
        eventName: event.name,
        eventDate: event.date,
        eventLocation: event.location
      };
    });
  });

  console.log('🎨 Total static items:', staticGalleryItems.length);
  console.log('🎨 Total dynamic items:', dynamicGalleryItems.length);
  console.log('🎨 Dynamic gallery items:', dynamicGalleryItems);

  // Combine static and dynamic items
  const allGalleryItems = [...staticGalleryItems, ...dynamicGalleryItems];
  console.log('🎨 Total gallery items:', allGalleryItems.length);

  const featuredVideo = allGalleryItems.find(item => item.isFeatured);
  const galleryMedia = allGalleryItems.filter(item => !item.isFeatured);

  // 8 items per slide
  const itemsPerSlide = 8;
  const totalSlides = Math.ceil(galleryMedia.length / itemsPerSlide);
  const currentSlideItems = galleryMedia.slice(
    currentSlideIndex * itemsPerSlide,
    (currentSlideIndex + 1) * itemsPerSlide
  );

  console.log('📊 Current slide items:', currentSlideItems.length);

  const openFullscreen = (media) => {
    setSelectedMedia(media);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setSelectedMedia(null);
    document.body.style.overflow = 'unset';
  };

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  if (loading) {
    return (
      <section id="gallery" className="py-16 md:py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-white mt-4">Loading gallery...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-16 md:py-24 bg-black">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}>
              Sneak Peak
            </h2>
            <div className="w-full h-2 bg-gradient-to-r from-orange-500 via-[#b8d4d4] to-orange-500 rounded-full mb-8"></div>
          </div>
          {/* {dynamicGalleryItems.length > 0 && (
            <p className="text-gray-400 text-sm">
              Showcasing {dynamicGalleryItems.length} images from {events.filter(e => e.images?.length > 0).length} events
            </p>
          )} */}
        </div>

        {/* Featured Video - With Side Spacing */}
        {featuredVideo && (
          <div className="mb-16 max-w-6xl mx-auto">
            <div 
              className="relative w-full aspect-video cursor-pointer group overflow-hidden rounded-2xl shadow-2xl"
              onClick={() => openFullscreen(featuredVideo)}
            >
              {featuredVideo.type === 'video' ? (
                <>
                  <video
                    className="w-full h-full object-cover"
                    poster={featuredVideo.thumbnail}
                    muted
                    loop
                    autoPlay
                    playsInline
                  >
                    <source src={featuredVideo.src} type="video/mp4" />
                  </video>
                </>
              ) : (
                <img
                  src={featuredVideo.src}
                  alt="Featured"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
              )}
            </div>
          </div>
        )}

        {/* Gallery Grid with Slider - Matches Featured Video Width */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {currentSlideItems.map((item) => (
              <div
                key={item.id}
                className="relative aspect-video cursor-pointer group overflow-hidden rounded-lg shadow-lg"
                onClick={() => openFullscreen(item)}
              >
                {item.type === 'video' ? (
                  <>
                    <video
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      poster={item.thumbnail}
                      muted
                      loop
                      autoPlay
                      playsInline
                    >
                      <source src={item.src} type="video/mp4" />
                    </video>
                  </>
                ) : (
                  <img
                    src={item.src}
                    alt={item.title || 'Gallery'}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => console.error('❌ Failed to load image:', item.src)}
                    onLoad={() => console.log('✅ Image loaded successfully:', item.src)}
                  />
                )}
                
                {/* Overlay with event name for dynamic images */}
                {item.eventName && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                    <div className="text-center">
                      <p className="text-white font-bold text-sm">{item.eventName}</p>
                      {item.eventDate && (
                        <p className="text-gray-300 text-xs mt-1">
                          {new Date(item.eventDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      )}
                      {item.eventLocation && (
                        <p className="text-gray-400 text-xs mt-1">{item.eventLocation}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Slider Navigation */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                disabled={currentSlideIndex === 0}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 bg-white p-4 rounded-full shadow-2xl hover:bg-orange-500 hover:text-white transition-all z-10 ${
                  currentSlideIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                disabled={currentSlideIndex === totalSlides - 1}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 bg-white p-4 rounded-full shadow-2xl hover:bg-orange-500 hover:text-white transition-all z-10 ${
                  currentSlideIndex === totalSlides - 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Slide Indicators */}
          {totalSlides > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlideIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlideIndex
                      ? 'bg-orange-500 w-8'
                      : 'bg-gray-600 w-2 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4"
          onClick={closeFullscreen}
        >
          <button
            className="absolute top-6 right-6 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all z-50"
            onClick={closeFullscreen}
          >
            <X className="w-8 h-8 text-white" />
          </button>

          <div className="relative max-w-7xl max-h-screen w-full h-full flex items-center justify-center flex-col" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'video' ? (
              <video
                className="max-w-full max-h-full rounded-lg"
                controls
                autoPlay
              >
                <source src={selectedMedia.src} type="video/mp4" />
              </video>
            ) : (
              <img
                src={selectedMedia.src}
                alt={selectedMedia.title || 'Fullscreen'}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
            
            {/* Event info in fullscreen */}
            {selectedMedia.eventName && (
              <div className="mt-4 text-center">
                <p className="text-white text-xl font-bold">{selectedMedia.eventName}</p>
                {selectedMedia.eventDate && (
                  <p className="text-gray-300 text-sm mt-1">
                    {new Date(selectedMedia.eventDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                )}
                {selectedMedia.eventLocation && (
                  <p className="text-gray-400 text-sm mt-1">{selectedMedia.eventLocation}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;